import { ActionIcon, Avatar, ConfirmExternalLinkModal, Link, Text, View } from '@/components'
import { CommunityComment } from '@/types'
import { formatDistanceToNowCustom } from '@/utils'
import { useCallback, useMemo } from 'react'
import { FeedUsername } from '../atoms/Username'

type FeedCommentProps = {
  comment: CommunityComment
  onReport?: () => void
  onReply?: () => void
}

const URL_REGEX = /\b((?:https?:\/\/|www\d{0,3}[.])[^\s()<>{}\[\]]+?(?=[.,;:!?]?(?:\s|$)))/gi

const isInternalUrl = (url: string) => {
  try {
    const u = new URL(url, window.location.origin)
    return u.hostname === 'app.hurd.world' || u.origin === window.location.origin
  } catch {
    return false
  }
}

const normalizeUrl = (url: string): string => {
  return /^https?:\/\//i.test(url) ? url : `https://${url}`
}

export const FeedComment = (props: FeedCommentProps) => {
  const {
    comment,
    onReport,
    onReply,
  } = props

  const handleLinkPress = useCallback(async (e: React.MouseEvent, internal: boolean, url: string) => {
    if (!internal) {
      e.preventDefault()

      const wantToOpen = await ConfirmExternalLinkModal.request()

      if (wantToOpen) {
        window.open(url, '_blank')
      }
    }
  }, [])

  const createdAt = useMemo(() => {
    return comment?.created_at ? formatDistanceToNowCustom(new Date(comment.created_at)) : null
  }, [comment?.created_at])

  const textElements = useMemo(() => {
    const parts = comment?.text?.split(URL_REGEX) ?? []

    return parts.map((part, index) => {
      if (URL_REGEX.test(part)) {
        const url = normalizeUrl(part)
        const internal = isInternalUrl(url)

        return (
          <Link
            key={index}
            to={url}
            openNewTab={!internal}
            variants={['commentLink']}
            onClick={e => handleLinkPress(e, internal, url)}
          >
            {part}
          </Link>
        )
      }

      return part
    })
  }, [comment?.text, handleLinkPress])

  if (!comment) return null

  return <View variants={['gap:1.2', 'paddingBottom:1']}>
    {!!comment?.profile && <Avatar debugName={`Feed comment avatar`} image={comment.profile?.avatar} name={comment.profile?.display_name} variants={['small']} />}

    <View variants={['column', 'flex']}>
      {!!comment?.profile && <FeedUsername username={comment.profile.display_name} />}
      <Text variants={['p3', 'color:neutral9', 'breakLine']}>
        {textElements}
      </Text>
      <View variants={['row', 'gap:1']}>
        {createdAt && <Text text={createdAt} variants={['p4', 'color:neutral9', 'light']} />}
        {onReply && <Text onPress={onReply} text={'Reply'} variants={['p4', 'color:neutral7', 'bold', 'cursor:pointer']} />}
      </View>
    </View>

    {
      onReport && (
        <ActionIcon
          debugName={`Report feed comment ${comment.id}`}
          name='report'
          onPress={onReport}
          variants={['minimal', 'neutral5', 'alignSelfEnd']}
        />
      )
    }
  </View>
}
